import * as React from "react";
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  placeholder?: 'blur' | 'empty';
  quality?: number;
  loading?: 'lazy' | 'eager';
  sizes?: string;
}

export const OptimizedImage = ({
  src,
  alt,
  className,
  width,
  height,
  priority = false,
  placeholder = 'blur',
  quality = 90,
  loading = 'lazy',
  sizes = '100vw',
  ...props
}: OptimizedImageProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '50px',
        threshold: 0.1 
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setImageLoaded(true);
  };

  const handleError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  // Create responsive image sources for better performance
  const createSrcSet = (baseSrc: string, width?: number) => {
    if (baseSrc.includes('http') || baseSrc.includes('data:')) {
      return baseSrc;
    }
    
    // Generate responsive sizes for local images
    if (width) {
      const sizes = [0.5, 1, 1.5, 2].map(scale => {
        const scaledWidth = Math.round(width * scale);
        return `${baseSrc} ${scaledWidth}w`;
      });
      return sizes.join(', ');
    }
    
    return baseSrc;
  };

  return (
    <div className={cn('relative overflow-hidden', className)} {...props}>
      {/* Placeholder */}
      {!imageLoaded && placeholder === 'blur' && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      
      {/* Actual Image with responsive srcset */}
      <img
        ref={imgRef}
        src={inView ? src : undefined}
        srcSet={inView && width ? createSrcSet(src, width) : undefined}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading={priority ? 'eager' : loading}
        decoding="async"
        {...(priority && { fetchpriority: 'high' as const })}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          'transition-opacity duration-300 ease-in-out',
          imageLoaded ? 'opacity-100' : 'opacity-0',
          imageError && 'opacity-50',
          'w-full h-full object-cover'
        )}
        style={{
          aspectRatio: width && height ? `${width}/${height}` : undefined,
        }}
      />
      
      {/* Error fallback */}
      {imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
          <span className="text-sm">Image not available</span>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;